import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Cookie, MonitorSmartphone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors w-fit">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">トップページに戻る</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">プライバシーポリシー</h1>
          </div>
          <p className="text-gray-500">最終更新日: 2026年1月1日</p>
        </div>

        <div className="space-y-6">
          <Card className="border border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-800">はじめに</CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600 leading-relaxed">
              <p>
                当サイト「アルバイト年収の壁シミュレーター」（以下、「当サイト」）は、ユーザーのプライバシーを尊重し、個人情報の保護に努めています。本プライバシーポリシーでは、当サイトがどのような情報を収集し、どのように使用するかについて説明します。
              </p>
            </CardContent>
          </Card>

          <Card className="border border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <Cookie className="w-5 h-5 text-blue-600" />
                Cookieの使用について
              </CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600 leading-relaxed space-y-4">
              <p>
                当サイトでは、ユーザー体験の向上およびアクセス解析のためにCookieを使用しています。Cookieとは、ウェブサイトがユーザーのブラウザに保存する小さなテキストファイルです。
              </p>
              <p>
                Cookieにより収集される情報には以下が含まれます：
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>アクセス日時</li>
                <li>閲覧したページ</li>
                <li>ブラウザの種類</li>
                <li>デバイス情報</li>
              </ul>
              <p>
                ユーザーはブラウザの設定によりCookieを無効にすることができますが、その場合、当サイトの一部機能が正常に動作しない可能性があります。
              </p>
            </CardContent>
          </Card>

          <Card className="border border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <MonitorSmartphone className="w-5 h-5 text-blue-600" />
                Google AdSenseについて
              </CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600 leading-relaxed space-y-4">
              <p>
                当サイトでは、第三者配信の広告サービス「Google AdSense」を利用しています。
              </p>
              <p>
                Google AdSenseは、ユーザーの興味に基づいた広告を表示するために、Cookieを使用することがあります。これにより、ユーザーが当サイトや他のサイトにアクセスした際の情報に基づいて、Googleやそのパートナーが適切な広告を表示します。
              </p>
              <p>
                ユーザーは、Googleの広告設定ページ（<a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.google.com/settings/ads</a>）にアクセスして、パーソナライズ広告を無効にすることができます。
              </p>
              <p>
                また、<a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.aboutads.info</a>にアクセスすることで、第三者配信事業者によるCookieの使用を無効にすることもできます。
              </p>
            </CardContent>
          </Card>

          <Card className="border border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-800">アクセス解析ツールについて</CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600 leading-relaxed space-y-4">
              <p>
                当サイトでは、Googleによるアクセス解析ツール「Google Analytics」を使用する場合があります。Google Analyticsはトラフィックデータの収集のためにCookieを使用しています。
              </p>
              <p>
                このトラフィックデータは匿名で収集されており、個人を特定するものではありません。この機能はCookieを無効にすることで収集を拒否することができますので、お使いのブラウザの設定をご確認ください。
              </p>
            </CardContent>
          </Card>

          <Card className="border border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-800">個人情報の取り扱いについて</CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600 leading-relaxed space-y-4">
              <p>
                当サイトでは、お問い合わせフォームを通じて、お名前やメールアドレスなどの個人情報を取得する場合があります。これらの情報は、お問い合わせへの回答以外の目的には使用いたしません。
              </p>
              <p>
                取得した個人情報は適切に管理し、法令に基づく場合を除き、第三者に提供することはありません。
              </p>
            </CardContent>
          </Card>

          <Card className="border border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-800">プライバシーポリシーの変更について</CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600 leading-relaxed">
              <p>
                当サイトは、必要に応じて本プライバシーポリシーを変更することがあります。変更後のプライバシーポリシーは、当サイトに掲載した時点から効力を生じるものとします。
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

const Footer = () => (
  <footer className="bg-gray-50 border-t border-gray-200 mt-12">
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-sm text-gray-500">© 2026 Silent Code.</p>
        <nav className="flex items-center gap-6">
          <Link to="/privacy" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">プライバシーポリシー</Link>
          <Link to="/about" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">運営者情報</Link>
          <Link to="/contact" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">お問い合わせ</Link>
        </nav>
      </div>
    </div>
  </footer>
);

export default PrivacyPolicy;
